import React, { useState } from 'react'
import type { VcsTrustEntry } from '@/lib/apiTypes'
import { api } from '@/lib/api'
import { Modal } from '@/shared/components/Modal'
import { AllowedRefsField } from '@/features/org/components/AllowedRefsField'

/** Tighten (or loosen) one trust entry's ref rule in place - no window where the repo is untrusted. */
export function EditAllowedRefsModal({ orgId, entry, onClose, onSaved }: {
  orgId: string
  entry: VcsTrustEntry
  onClose: () => void
  onSaved: () => void
}) {
  // A legacy "any ref" entry opens with `main` pre-filled: the point of opening this modal on
  // such a row is almost always to tighten it, and the checkbox is one click away if not.
  const [allowedRefs, setAllowedRefs] = useState<string[] | null>(entry.allowed_refs === null ? ['main'] : entry.allowed_refs)
  const [busy, setBusy]   = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (allowedRefs !== null && allowedRefs.length === 0) {
      setError('List at least one ref, or tick "Allow pushes from any ref".')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.patch(`/api/orgs/${orgId}/vcs-trust/${entry.id}`, { allowed_refs: allowedRefs })
      onSaved()
      onClose()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      track="vcs_trust_edit_refs"
      title="Allowed refs"
      subtitle={entry.subject}
      kicker="// pipeline trust"
      onClose={onClose}
      dismissable={!busy}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>Cancel</button>
          <button form="edit-allowed-refs-form" type="submit" className="btn btn-primary btn-sm" disabled={busy}>
            {busy ? <span className="spinner" /> : 'Save'}
          </button>
        </div>
      }
    >
      <form id="edit-allowed-refs-form" onSubmit={(e) => { void handleSubmit(e) }}>
        {entry.allowed_refs === null && (
          <p style={{ fontSize: '0.78rem', color: 'var(--amber)', marginTop: 0, marginBottom: 12 }}>
            This entry predates ref rules and currently allows pushes from any branch, tag or pull request.
          </p>
        )}
        <AllowedRefsField value={allowedRefs} onChange={setAllowedRefs} autoFocus />
        {error && <div className="feedback-error" style={{ marginTop: 12 }}>{error}</div>}
      </form>
    </Modal>
  )
}
